import { useSyncExternalStore } from "react";
import {
  ADULT_MIN_AGE,
  ADULT_VERIFICATION_STORAGE_KEY,
  ADULT_VERIFIED_EVENT,
  LEGACY_ADULT_VERIFIED_KEY,
} from "./constants";
import { applyAdultVerifiedUiState } from "./adultBadgeService";
import { isAdultOpenLink } from "./adultLinkService";
import { PageNode } from "../../types/PageNode";

export const getAdultVerified = (): boolean => {
  try {
    const storedValue = localStorage.getItem(ADULT_VERIFICATION_STORAGE_KEY);

    if (storedValue !== null) {
      return storedValue === "true";
    }

    if (localStorage.getItem(LEGACY_ADULT_VERIFIED_KEY) === "true") {
      localStorage.setItem(ADULT_VERIFICATION_STORAGE_KEY, "true");
      localStorage.removeItem(LEGACY_ADULT_VERIFIED_KEY);
      return true;
    }
  } catch {
    return false;
  }

  return false;
};

/** Preview, published form view, or other visitor-facing modes — not the editor canvas */
export const isAdultInteractiveViewMode = (
  isPreview: boolean,
  isFormLocation: boolean,
): boolean => isPreview || isFormLocation;

const verifiedListeners = new Set<() => void>();

const emitVerifiedChange = (): void => {
  verifiedListeners.forEach((listener) => listener());
};

export const subscribeAdultVerified = (listener: () => void): (() => void) => {
  verifiedListeners.add(listener);

  const onVerifiedEvent = (): void => listener();
  const onStorage = (event: StorageEvent): void => {
    if (
      event.key === ADULT_VERIFICATION_STORAGE_KEY ||
      event.key === LEGACY_ADULT_VERIFIED_KEY ||
      event.key === null
    ) {
      listener();
    }
  };

  window.addEventListener(ADULT_VERIFIED_EVENT, onVerifiedEvent);
  window.addEventListener("storage", onStorage);

  return () => {
    verifiedListeners.delete(listener);
    window.removeEventListener(ADULT_VERIFIED_EVENT, onVerifiedEvent);
    window.removeEventListener("storage", onStorage);
  };
};

export const useAdultVerified = (): boolean =>
  useSyncExternalStore(subscribeAdultVerified, getAdultVerified, () => false);

/**
 * Persists verification in this browser only (localStorage).
 * Never mutates page JSON or triggers backend saves.
 */
export const setAdultVerified = (): void => {
  localStorage.setItem(ADULT_VERIFICATION_STORAGE_KEY, "true");
  localStorage.removeItem(LEGACY_ADULT_VERIFIED_KEY);
  emitVerifiedChange();
  window.dispatchEvent(new CustomEvent(ADULT_VERIFIED_EVENT));
};

export const isBirthYearValid = (year: number): boolean => {
  if (!Number.isFinite(year)) return false;
  const currentYear = new Date().getFullYear();
  if (year > currentYear) return false;
  return year <= currentYear - ADULT_MIN_AGE;
};

export const isBirthYearUnderAge = (year: number): boolean => {
  if (!Number.isFinite(year)) return false;
  const currentYear = new Date().getFullYear();
  if (year > currentYear) return false;
  return year > currentYear - ADULT_MIN_AGE;
};

export const getBirthYearOptions = (): number[] => {
  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 100;
  const years: number[] = [];
  for (let year = currentYear; year >= minYear; year -= 1) {
    years.push(year);
  }
  return years;
};

export type AdultVerificationModalMode = "link" | "pageGate";

type AdultVerificationModalState = {
  isOpen: boolean;
  pendingUrl: string | null;
  mode: AdultVerificationModalMode;
};

let modalState: AdultVerificationModalState = {
  isOpen: false,
  pendingUrl: null,
  mode: "link",
};

export const getIsAdultPageGateEnabled = (
  checkboxAge18?: boolean | null,
): boolean => Boolean(checkboxAge18);

const modalListeners = new Set<() => void>();

const emitModalChange = (): void => {
  modalListeners.forEach((listener) => listener());
};

export const getAdultVerificationModalState = (): AdultVerificationModalState =>
  modalState;

export const subscribeAdultVerificationModalState = (
  listener: () => void,
): (() => void) => {
  modalListeners.add(listener);
  return () => modalListeners.delete(listener);
};

export const useAdultVerificationModalState = (): AdultVerificationModalState =>
  useSyncExternalStore(
    subscribeAdultVerificationModalState,
    getAdultVerificationModalState,
  );

const openAdultUrl = (url: string): void => {
  window.open(url, "_blank", "noopener,noreferrer");
};

export const openAdultVerificationModal = (url: string): void => {
  if (getAdultVerified()) {
    openAdultUrl(url);
    return;
  }

  modalState = { isOpen: true, pendingUrl: url, mode: "link" };
  emitModalChange();
};

export const openAdultPageGateModal = (): void => {
  if (getAdultVerified()) {
    return;
  }

  modalState = { isOpen: true, pendingUrl: null, mode: "pageGate" };
  emitModalChange();
};

export const closeAdultVerificationModal = (): void => {
  modalState = { isOpen: false, pendingUrl: null, mode: "link" };
  emitModalChange();
};

export const leaveAdultVerificationPage = (): void => {
  const targetWindow =
    window.top && window.top !== window.self ? window.top : window;

  try {
    targetWindow.close();
  } catch {}

  if (targetWindow.history.length > 1) {
    targetWindow.history.back();
    return;
  }

  targetWindow.location.replace("about:blank");
};

export const confirmAdultVerificationModal = (birthYear: number): boolean => {
  const { pendingUrl, mode } = modalState;

  if (!isBirthYearValid(birthYear)) {
    return false;
  }

  if (mode === "link" && !pendingUrl) {
    return false;
  }

  setAdultVerified();
  if (pendingUrl) {
    openAdultUrl(pendingUrl);
  }
  closeAdultVerificationModal();
  return true;
};

export const shouldShowAdultPageGate = (
  checkboxAge18: boolean | null | undefined,
): boolean => getIsAdultPageGateEnabled(checkboxAge18) && !getAdultVerified();

export const handleAdultLinkClick = (
  event: Event,
  url: string,
  isInteractiveView = true,
): void => {
  event.preventDefault();
  event.stopPropagation();

  if (getAdultVerified()) {
    openAdultUrl(url);
    return;
  }

  if (!isInteractiveView) {
    return;
  }

  openAdultVerificationModal(url);
};

type AdultLinkMouseEvent = {
  nativeEvent: unknown;
  preventDefault: () => void;
  stopPropagation: () => void;
};

/** Capture-phase handler for container nodes (div blocks) with nested children */
export const handleAdultLinkCaptureClick = (
  event: AdultLinkMouseEvent,
  node: PageNode,
  isInteractiveView: boolean,
): boolean => {
  if (
    !isInteractiveView ||
    !isAdultOpenLink(node) ||
    !node.onClickAction?.value
  ) {
    return false;
  }

  handleAdultLinkClick(
    event.nativeEvent as Event,
    node.onClickAction.value,
    true,
  );
  return true;
};

export const syncAdultVerifiedUiForView = (
  isPreview: boolean,
  isFormLocation: boolean,
  isPublishCapture = false,
): void => {
  applyAdultVerifiedUiState(
    getAdultVerified(),
    isAdultInteractiveViewMode(isPreview, isFormLocation),
    isPublishCapture,
  );
};
